import {
  Button,
  Col,
  Container,
  getTypographyDefaultColor,
  ImageSourceSizes,
  Modal,
  Row,
  Typography
} from '@ag1/ui';
import clsx from 'clsx';
import { useMemo, useState } from 'react';
import IngredientModal from 'shared/modals/IngredientModal/IngredientModal';
import { CustomPortableText } from 'shared/studio/PortableText';
import { getThemeCSSClass } from 'shared/utils/getThemeCSSClass';
import { SanityKeyed, TextAndTable as TextAndTableProps } from 'types/studio/studio';
import { StudioBaseImageV2 } from '../StudioBaseImage/StudioBaseImage';
import { CommonSectionProps } from '../types';
import { UnifiedButton, UnifiedButtonType } from '../UnifiedButton';
import styles from './TextAndTable.module.scss';

const IMAGE_SIZES = { xxs: '100vw', s: '50vw', xl: '650px' } satisfies ImageSourceSizes;

const TextAndTable = ({
  _key: key,
  _type: sectionType,
  headline,
  bodyText,
  media,
  theme,
  table,
  buttonCTA,
  visibleOnPageLoad
}: SanityKeyed<TextAndTableProps> & CommonSectionProps) => {
  const [isOpen, setOpen] = useState(false);

  const hasUnifiedButton = useMemo(() => !!buttonCTA?.unifiedButton?.openModalButton, [buttonCTA]);

  return (
    <>
      <div className={clsx(styles.root, getThemeCSSClass(theme))} id={key}>
        <Container narrow>
          <Row align="between" className={styles.row}>
            <Col>
              <Typography
                variant="headlineSans"
                size="2"
                component="h2"
                color={getTypographyDefaultColor(theme)}
                noPadding>
                {headline}
              </Typography>
              <Typography variant="subtitle" size="2" color={getTypographyDefaultColor(theme)}>
                <CustomPortableText value={bodyText} />
              </Typography>
              {media?.image && (
                <StudioBaseImageV2
                  className={styles.mobileImage}
                  image={media.image}
                  mobileImage={media.mobileImage}
                  tabletImage={media.tabletImage}
                  withMobileImage={media.withMobileImage}
                  sizes={IMAGE_SIZES}
                  eagerLoading={visibleOnPageLoad}
                />
              )}
              <div className={styles.table}>
                {table?.options?.map((option) => (
                  <Row
                    key={option._key}
                    className={clsx(styles['border-bottom'], styles.rowGap)}
                    align="between">
                    <Col noGutter>
                      <Typography
                        variant="body"
                        size="2"
                        color={getTypographyDefaultColor(theme)}
                        className={clsx(styles['text-uppercase'], styles.padding)}>
                        {option.title}
                      </Typography>
                    </Col>
                    <Col noGutter className={styles.col}>
                      <Typography
                        variant="body"
                        size="2"
                        color={getTypographyDefaultColor(theme)}
                        className={clsx(styles['text-right'], styles.padding)}>
                        {option.value}
                      </Typography>
                    </Col>
                  </Row>
                ))}
              </div>
              {hasUnifiedButton ? (
                <UnifiedButton
                  unifiedButton={buttonCTA?.unifiedButton as UnifiedButtonType}
                  sectionType={sectionType}
                  size="medium"
                  theme="new"
                  className={styles.button}
                />
              ) : (
                buttonCTA?.modalImage &&
                buttonCTA?.cta === 'modal' && (
                  <Button
                    size={{ l: 'large', xxs: 'medium' }}
                    rounded
                    fluid
                    variant={buttonCTA?.modalButton?.variant}
                    target="_self"
                    theme={theme}
                    data-gtm-skip-global
                    icon={
                      buttonCTA?.modalButton?.showIcon && buttonCTA?.modalButton?.iconOptions?.icon
                    }
                    iconPosition={buttonCTA?.modalButton?.iconOptions?.position}
                    className={styles.button}
                    onClick={() => setOpen(true)}>
                    {buttonCTA?.modalButton?.portableText && (
                      <CustomPortableText value={buttonCTA?.modalButton?.portableText} />
                    )}
                  </Button>
                )
              )}
            </Col>
            <Col className={styles.image}>
              {media?.image && (
                <StudioBaseImageV2
                  image={media.image}
                  mobileImage={media.mobileImage}
                  tabletImage={media.tabletImage}
                  withMobileImage={media.withMobileImage}
                  sizes={IMAGE_SIZES}
                  eagerLoading={visibleOnPageLoad}
                />
              )}
            </Col>
          </Row>
        </Container>
      </div>
      <Modal isOpen={isOpen} onClose={() => setOpen(false)} mode="fullScreen">
        <div className={styles.modalInner}>
          {buttonCTA?.modalImage?.image && (
            <IngredientModal
              isOpen={isOpen}
              onClose={() => setOpen(false)}
              isV53={buttonCTA?.unifiedButton?.v53Toggle}
              ingredientProducts={buttonCTA?.unifiedButton?.ingredientProducts}
              supplementType={buttonCTA?.unifiedButton?.supplementType}
              showFlavors={buttonCTA?.unifiedButton?.showFlavors}
            />
          )}
        </div>
      </Modal>
    </>
  );
};

export default TextAndTable;
